<template>
  <div class="approve__container">
    <change-status-header :displayName="data.displayName" />
    <div class="approve__content">
      <mds-loader v-if="loading" />
      <div class="approve__data" v-else>
        <div class="shareclasses-data__nonapprove">
          <change-status-table :columns="['Ticker', 'Status', 'Category']">
            <template slot="title">
              Non-approved Funds
            </template>
            <template v-for="(shareClass, index) in shareClasses">
              <approve-shareclass
                v-if="!shareClass.approved"
                :class="{ 'group-begin': true, 'group-end': selectedClient }"
                :key="`non-approved-shareclass-${index}`"
                :ticker="shareClass.ticker"
                :statusArray="shareClass.statusArray"
                :statusText="shareClass.status"
                :options="shareClass.options"
                :newStatus="shareClass.newStatus"
                :newStatusIcon="shareClass.newStatusIcon"
                :newStatusText="shareClass.newStatusText"
                :categories="categories"
                :morningstarCategory="shareClass.morningstarCategory"
                :newCategory="shareClass.newCategory"
                :selected="false"
                @onSelectStatus="selectNewStatusShareclass($event, index)"
                @onSelectCategory="selectNewCategoryShareclass($event, index)"
              >
                {{ shareClass[shareclassDisplayName] }}
              </approve-shareclass>
            </template>
          </change-status-table>
        </div>
        <div class="approve__actions">
          <mds-button variation="primary" class="approve-funds-button"
            :disabled="approveButtonDisabled" @click="approve"
          >
            Approve Funds
          </mds-button>
        </div>
        <div class="shareclasses-data__approve" v-if="anyApprovedShareclass">
          <change-status-table :columns="['Ticker', 'Status', 'Category']">
            <template slot="title">
              Approved Funds
            </template>
            <template v-for="(shareClass, index) in shareClasses">
              <approve-shareclass
                v-if="shareClass.approved"
                :class="{ 'group-begin': true, 'group-end': selectedClient }"
                :key="`approved-shareclass-${index}`"
                :ticker="shareClass.ticker"
                :statusArray="shareClass.statusArray"
                :statusText="shareClass.status"
                :options="shareClass.options"
                :newStatus="shareClass.newStatus"
                :newStatusIcon="shareClass.newStatusIcon"
                :newStatusText="shareClass.newStatusText"
                :categoryText="getShareClassCategoryText(shareClass)"
                :isPending="shareClass.isPending"
                :selected="true"
              >
                {{ shareClass[shareclassDisplayName] }}
              </approve-shareclass>
            </template>
          </change-status-table>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import {
  includes,
  find,
  get,
  orderBy,
  cloneDeep,
} from 'lodash';

import ErrorNotificationMixin from '@/mixins/ErrorNotificationMixin';
import ChangeStatusHeader from '@/components/atoms/ChangeStatusHeader';
import ChangeStatusTable from '@/components/atoms/ChangeStatusTable';
import ApproveShareclass from '@/components/molecules/ApproveShareclass';
import {
  extractNameByMayorCount,
  getSchemaIndexFromStatus,
  applySchemaToOptions,
  goToLogoutRoute,
} from '@/utils';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import * as ApprovalsService from '@/services/Approvals';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import * as CategoriesService from '@/services/Categories';

/* eslint import/no-cycle: [2, { maxDepth: 1 }] */
import * as ShareClassesService from '@/services/ShareClasses';

/**
 * Modal Content
 * @Component('approve')
 */
export default {
  name: 'approve',
  components: {
    ChangeStatusHeader,
    ChangeStatusTable,
    ApproveShareclass,
  },
  mixins: [
    ErrorNotificationMixin({ component: 'Modal:Organisms/Approve' }),
  ],
  props: {
    data: {
      type: Object,
    },
  },
  data() {
    return {
      loading: true,
      processing: false,
      shareClasses: [],
      categories: [],
      anyApprovedShareclass: false,
      disableSubmit: true,
      defaultOptions: [
        {
          label: 'Select',
          icon: '',
          config: {
            show: true,
            disabled: false,
          },
          value: 0,
        },
        {
          label: 'Approved',
          icon: 'Approved',
          config: {
            show: true,
            disabled: false,
          },
          value: 1,
        },
        {
          label: 'On Watch',
          icon: 'OnWatch',
          config: {
            show: true,
            disabled: false,
          },
          value: 3,
        },
        {
          label: 'Removed',
          icon: 'Removed',
          config: {
            show: true,
            disabled: false,
          },
          value: 2,
        },
      ],
      schema: [
        {
          status: ['*'], // Multiple statuses
          options: ['Select', 'Approved', 'On Watch', 'Removed'],
        },
        {
          status: ['None'],
          options: [
            'Select',
            'Approved',
            'On Watch',
            {
              label: 'Removed',
              config: { disabled: true },
            },
          ],
        },
        {
          status: ['Removed'],
          options: [
            'Select',
            'Approved',
            'On Watch',
            {
              label: 'Removed',
              config: { disabled: true },
            },
          ],
        },
      ],
      request: null,
      requestController: null,
      requestUrl: null,
    };
  },
  computed: {
    ...mapState({
      fiduciary: (state) => state.user.fiduciary.id,
      selectedClient: (state) => state.grid.client.selected,
      clientName: (state) => state.grid.client.name,
    }),
    fundCategory() {
      return extractNameByMayorCount(this.data.psaCategory, 'category.name');
    },
    shareclassDisplayName() {
      return 'fundName';
    },
    approveButtonDisabled() {
      return this.processing || this.disableSubmit;
    },
  },
  async created() {
    let categoriesResponse = {};
    try {
      categoriesResponse = await CategoriesService.getFundCategoriesByClient({
        selectedClient: this.selectedClient,
      });
    } catch (error) {
      const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
      if (httpStatus === 401) {
        goToLogoutRoute(this.$route, this.$router);
      }
      return -1;
    }

    const { data: categoriesData } = categoriesResponse;
    this.categories = orderBy(categoriesData, 'name');

    if (!this.data.__isTargetSeries) {
      ShareClassesService.getShareClassesByClientAndFundId({
        selectedClient: this.selectedClient,
        fundID: this.data.fundID,
      }).then((result) => {
        if (result.status.success) {
          const { data = [] } = result;
          this.shareClasses = this._formatData(data);
        }
        this.loading = false;
      }).catch((error) => {
        const httpStatus = (error?.response.status) ? (error?.response.status) : error?.status;
        if (httpStatus === 401) {
          this.$router.push('/login');
        } else {
          this.notifyError({
            error,
            section: 'methods:approve',
            data: { url: this.requestUrl },
            notificaction: {
              title: 'Fetch Share Classes Failed',
              message: `Error: ${error.message}`,
            },
          });
        }
        return false;
      });
    } else {
      this.shareClasses = this._formatData(this.data.subs);
      this.loading = false;
    }
    return 0;
  },
  methods: {
    selectNewStatusShareclass(...args) {
      const [newStatus, index] = args;
      this.shareClasses[index].newStatus = newStatus;
    },
    selectNewCategoryShareclass(...args) {
      const [newCategory, index] = args;
      this.shareClasses[index].newCategory = newCategory;
    },
    getShareClassCategoryText(shareClass) {
      if (shareClass.__isFundSeries) {
        return get(shareClass, 'psaCategory[0].category.name', '');
      }
      return shareClass.psaCategory && shareClass.psaCategory.name;
    },
    getShareClassCategory(shareClass) {
      if (shareClass.__isFundSeries) {
        return get(shareClass, 'psaCategory[0].category', {});
      }
      return shareClass.psaCategory || {};
    },
    _formatData(data) {
      const clonedData = cloneDeep(data) || [];
      return clonedData
        // If have pendingStatus
        .map((item) => {
          if (item.pendingStatus) {
            return {
              ...item,
              status: item.__isFundSeries ? item.status : item.pendingStatus,
            };
          }
          return item;
        })
        // Transform shareclass data
        .map((item) => {
          /* eslint-disable no-param-reassign */
          // Set current status
          item.statusArray = [item.status];
          item.statusId = item.status.id;
          item.status = item.status.name;

          // Mark as Non-Approved Shareclass
          item.approved = !includes(['None', 'Removed'], item.status);
          if (item.approved) {
            // Switch to show the second group table
            this.anyApprovedShareclass = true;
          } else {
            // Set default new status
            item.newStatus = 0;
            item.newStatusText = '';
            item.newStatusIcon = '';

            // Options for change status
            const statuses = item.statusArray.map((el) => el.name);
            const index = getSchemaIndexFromStatus(this.schema, statuses);
            const options = applySchemaToOptions(
              this.schema[index].options,
              this.defaultOptions,
            );
            item.options = options;

            // TODO: Remove this block when the morningstarCategory populated from PSA Manager
            if (!item.morningstarCategory) {
              // If the morninstarCategory is not provided assign default values
              item.morningstarCategory = { id: 0, name: '' };
            }

            // Check if Morningstar Category is in the FiduciaryList
            item.morningstarCategory.found = find(this.categories, {
              name: get(item.morningstarCategory, 'name', ''),
            });

            // By default the Morningstar Category is selected
            if (item.__isFundSeries) {
              const shareClassCategory = this.getShareClassCategory(item);
              item.newCategory = get(shareClassCategory, 'id');
              item.newCategoryName = get(shareClassCategory, 'name');
            } else if (item.morningstarCategory.found) {
              item.newCategory = item.morningstarCategory.id;
              item.newCategoryName = get(item.morningstarCategory, 'name', '');
            } else {
              item.newCategory = 0;
              item.newCategoryName = '';
            }
          }
          /* eslint-enable no-param-reassign */
          return item;
        });
    },
    _haveSomeChangeStatus(shareclasses) {
      // Check if any non approve shareclasses have a different status or category from the default
      this.disableSubmit = !shareclasses.some(
        (item) => !item.approved && item.newStatus !== 0 && item.newCategory !== 0,
      );
    },
    _formatOneClientPayload(items) {
      const payload = [];
      items.forEach((item) => {
        /* eslint-disable no-param-reassign */
        if (!item.approved) {
          const object = {
            fiduciaryFundID: item.fiduciaryFundID,
          };

          object.fiduciaryClient = item.fiduciaryClient;
          object.updatedStatus = {
            id: item.newStatus,
            name: item.newStatusIcon,
          };

          // If the Morningstar Category is in the Category List
          if (item.morningstarCategory && item.morningstarCategory.found) {
            // Check if the Category Selected is the Morningstar Category
            if (item.newCategory === item.morningstarCategory.id) {
              // Replace for the equivalent in the Category List
              item.newCategory = item.morningstarCategory.found.id;
              item.newCategoryName = item.morningstarCategory.found.name;
            }
          }
          // Extract Category Name from the selected id
          if (item.newCategory && item.newCategory !== 0 && item.newCategoryName === '') {
            item.newCategoryName = find(this.categories, {
              id: parseInt(item.newCategory, 10),
            }).name;
          }
          object.updatedCategory = {
            id: item.newCategory,
            name: item.newCategoryName,
          };

          // Add change if the client:
          // Have a new status and have a new category
          if (item.newStatus !== 0 && item.newCategory !== 0) {
            payload.push(object);
          }
        }
        /* eslint-enable no-param-reassign */
      });
      return payload;
    },
    approve() {
      const payload = this._formatOneClientPayload(this.shareClasses);

      // If any changes for submit
      if (payload.length) {
        ApprovalsService.importFundApprovals({
          payload,
        }).then((result) => {
          if (result.status.success) {
            this.$store.dispatch('performActionFullscreenModal', true);
            this.$store.dispatch('closeFullscreenModal');
            this.$store.dispatch('openNotification', {
              message: 'Status Change Added to Pending',
            });
          }
          return result;
        }).catch((error) => {
          const httpStatus = (error?.response.status) ? (error?.response.status) : (error?.status);
          if (httpStatus === 401) {
            this.$router.push('/login');
          } else {
            this.notifyError({
              error,
              section: 'methods:approve',
              data: { url: this.requestUrl },
              notificaction: {
                title: 'Fund Approval Failed',
                message: `Error: ${error.message}`,
              },
            });
          }
          return false;
        }).finally(() => {
          this.processing = false;
        });
      } else {
        this.processing = false;
      }
    },
  },
  watch: {
    shareClasses: {
      handler(newValue) {
        /* eslint-disable no-param-reassign */
        newValue.map((item) => {
          if (!item.approved) {
            const option = find(this.defaultOptions, { value: item.newStatus });
            item.newStatusText = option.label;
            item.newStatusIcon = option.icon;
          }
          return item;
        });
        /* eslint-enable no-param-reassign */
        this._haveSomeChangeStatus(newValue);
      },
      deep: true,
    },
  },
  beforeDestroy() {
    this.requestController && this.requestController.abort();
  },
};
</script>
<style lang="scss">
.approve {
  &__container {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
  }
  &__content {
    $box-height: calc(100% - 108px - 16px); // Substract: header height + actions height
    display: flex;
    flex-direction: column;
    flex: auto;
    min-height: $box-height;
    max-height: $box-height;
    padding: $mds-space-4-x $mds-space-8-x;
  }
  &__data {
    flex: auto;
    margin-top: $mds-space-inset-2-x;
    overflow-x: hidden;
    overflow-y: auto;
  }
  &__actions {
    flex: initial;
    padding-bottom: $mds-space-inset-4-x;
    text-align: right;
  }
  &__spacer {
    padding: $mds-space-3-x 0;
  }
}
.shareclasses-data {
  &__nonapprove {
    margin-bottom: $mds-space-4-x;
  }
  &__approve {
    background-color: $mds-color-neutral-95;
    padding-top: $mds-space-4-x;
  }
}
</style>
